import {
  useEffect, forwardRef, useState, useMemo
} from 'react';

import ConfettiItems from './ConfettiItems';
import { WheelProvider } from './wheelContext';
import { useStreamData } from '@lib/hooks/useStreamData';
import useInteractionSound from '@components/Presentation/hooks/useInteractionSound';

import KeywordWheel from './wheelTypes/Keyword';
import WheelResultView from './wheelTypes/ResultView';
import InteractionWheel from './wheelTypes/Interaction';
import ChoiceCircleWheel from './wheelTypes/ChoiceCircle';
import PreviewWheel from './wheelTypes/Preview';

const WinnerWheel = forwardRef((props, ref) => {
  const {
    isMuted,
    streamId,
    splitDiv,
    layoutMode,
    stopFeature,
    setIsStarted,
    platformType,
    updateFSMeta,
    isFullScreen,
    setShowRosModel,
    interactionType,
    currentRosResult,
    startInteraction,
    interactionState,
    currentRosFeature,
    resetResultClicked,
    setInteractionState,
    setCurrentRosResult,
    setStartInteraction,
    setResetResultClicked,
    showInstantCustomisationPopup,
  } = props;

  const { type, setting } = currentRosFeature;

  const [isResultView, setIsResultView] = useState(false);
  const { hiddenData, setHiddenData } = useStreamData(s => ({
    hiddenData: s.hiddenData,
    setHiddenData: s.setHiddenData
  }));
  useEffect(() => {
    window.dispatchEvent(new Event('resize'));
  }, [isFullScreen, splitDiv]);

  useEffect(() => {
    const winnerInfo = currentRosResult?.[0]?.options?.find((opt) => opt.isWinner);
    if (winnerInfo) { setIsResultView(true); } else { setIsResultView(false); }
  }, [currentRosResult?.[0]?.options]);

  useEffect(() => {
    if (currentRosFeature?.id) { setShowRosModel(false); }
  }, [currentRosFeature]);

  const featureSettings = useMemo(() => ({
    ...currentRosFeature,
    setting: {
      ...setting,
      layoutMode
    }
  }), [currentRosFeature, layoutMode]);

  const { playSound } = useInteractionSound({
    featureSettings: { ...featureSettings, type: 'wheelSpin' },
    soundKey: 'wheelSpinSoundOptions',
    isEnabledKey: 'isWheelSpinSoundEnabled',
    isMuted,
    options: {
      volume: 0.5,
      playbackRate: 1
    }
  });

  const { playSound: playButtonSound } = useInteractionSound({
    featureSettings: { ...featureSettings, type: 'wheelButton' },
    soundKey: 'wheelButtonSoundOptions',
    isEnabledKey: 'isWheelButtonSoundEnabled',
    isMuted,
    options: {
      volume: 1.5,
      playbackRate: 1,
    }
  });

  const { playSound: playResultSound } = useInteractionSound({
    featureSettings: { ...featureSettings, type: 'wheelResult' },
    soundKey: 'wheelResultSoundOptions',
    isEnabledKey: 'isWheelResultSoundEnabled',
    isMuted,
    options: {
      volume: 0.5,
      playbackRate: 1,
      duration: 3000
    }
  });

  const [poolingInteraction, setForPoolingInteraction] = useState(false);

  const wheelProps = {
    splitDiv,
    layoutMode,
    stopFeature,
    isFullScreen,
    isResultView,
    setShowRosModel,
    interactionState,
    poolingInteraction,
    setCurrentRosResult,
    showInstantCustomisationPopup,

    playSound,
    playButtonSound,
    playResultSound,
    setIsResultView,
    setForPoolingInteraction,
  };

  const isPreview = (window?.location?.href?.includes('preview') || window?.location?.href?.includes('sandbox'));

  return (
    <WheelProvider
      streamId={streamId}
      hiddenData={hiddenData}
      platformType={platformType}
      setIsStarted={setIsStarted}
      updateFSMeta={updateFSMeta}
      setHiddenData={setHiddenData}
      interactionType={interactionType}
      currentRosResult={currentRosResult}
      interactionState={interactionState}
      startInteraction={startInteraction}
      currentRosFeature={currentRosFeature}
      poolingInteraction={poolingInteraction}
      resetResultClicked={resetResultClicked}
      setStartInteraction={setStartInteraction}
      setInteractionState={setInteractionState}
      setResetResultClicked={setResetResultClicked}
      setForPoolingInteraction={setForPoolingInteraction}
    >
      <div id='choice-circle-div' className={'w-full h-full'}>
        <div className='relative h-full rounded-lg overflow-hidden'>
          <div className={'h-full flex justify-center items-center'}>
            <ConfettiItems
              {...wheelProps}
            />
            {isResultView ?
              <WheelResultView
                {...wheelProps}
              /> :
              isPreview && type === 'newWheel' ?
                <PreviewWheel
                  {...wheelProps}
                /> :
                type === 'wheel' || setting?.wheelSelectionType === 'customNames' ?
                  <ChoiceCircleWheel
                    {...wheelProps}
                  /> :
                  (setting?.enableKeyword && type === 'newWheel') || poolingInteraction ?
                    <KeywordWheel
                      {...wheelProps}
                    /> :
                    <InteractionWheel
                      {...wheelProps}
                    />
            }
          </div>
        </div>
      </div>
    </WheelProvider>
  );
});

export default WinnerWheel;
